import React, { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Trash2, Edit2, X, Phone, MessageCircle, Camera, ArrowUpDown } from 'lucide-react'
import { getCustomers, createCustomer, updateCustomer, deleteCustomer } from '../../services/staffServices'
import { uploadToStorage } from '../../lib/supabase'
import StaffLayout from './components/StaffLayout'
import toast from 'react-hot-toast'
import { StaggerContainer, StaggerItem, ModalOverlay, ButtonLoader } from './components/StaffAnimations'

const CUSTOMER_TYPES = [
    'MAHINDRA', 'OLA', 'ATHER', 'AMPERE', 'ZILIO', 'ETVS',
    'BSNL', 'HOME_UPS', 'ADVANTEX_BATTERY', 'TOTO_BATTERY', 'OTHERS',
]
const BSNL_TYPES = ['BSNL']

// Avatar colors per customer type
const TYPE_COLORS = {
    MAHINDRA: 'bg-red-600',
    OLA: 'bg-green-600',
    ATHER: 'bg-blue-600',
    AMPERE: 'bg-purple-600',
    ZILIO: 'bg-amber-600',
    ETVS: 'bg-teal-600',
    BSNL: 'bg-orange-600',
    HOME_UPS: 'bg-indigo-600',
    ADVANTEX_BATTERY: 'bg-pink-600',
    TOTO_BATTERY: 'bg-rose-600',
    OTHERS: 'bg-slate-600',
}

const AVATAR_COLORS = ['bg-blue-600', 'bg-purple-600', 'bg-green-600', 'bg-amber-600', 'bg-rose-600', 'bg-indigo-600', 'bg-teal-600']

function CustomerAvatar({ customer, size = 10, textSize = 'text-base' }) {
    const colorIdx = customer.customer_name ? customer.customer_name.charCodeAt(0) % AVATAR_COLORS.length : 0
    const px = size * 4
    if (customer.photo_url) {
        return (
            <div className="rounded-full overflow-hidden shrink-0 bg-slate-800" style={{ width: px, height: px }}>
                <img
                    src={customer.photo_url}
                    alt={customer.customer_name}
                    className="w-full h-full object-cover object-top"
                />
            </div>
        )
    }
    return (
        <div className={`rounded-full ${AVATAR_COLORS[colorIdx]} flex items-center justify-center ${textSize} font-bold text-white shrink-0`} style={{ width: px, height: px }}>
            {customer.customer_name?.[0]?.toUpperCase() || '?'}
        </div>
    )
}

// Format date DD/MM/YYYY
function formatDateIn(isoOrDdmm) {
    if (!isoOrDdmm) return ''
    // If already DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(isoOrDdmm)) return isoOrDdmm
    // Convert YYYY-MM-DD to DD/MM/YYYY
    const [y, m, d] = isoOrDdmm.split('-')
    if (y && m && d) return `${d}/${m}/${y}`
    return isoOrDdmm
}

function toDdmmyyyy(isoDate) {
    if (!isoDate) return ''
    const [y, m, d] = isoDate.split('-')
    return `${d}/${m}/${y}`
}

function toIso(ddmm) {
    if (!ddmm) return ''
    if (/^\d{4}-\d{2}-\d{2}$/.test(ddmm)) return ddmm
    const [d, m, y] = ddmm.split('/')
    return `${y}-${m}-${d}`
}

function isBsnl(type) { return type === 'BSNL' }

function PhotoUploadButton({ photoUrl, onUpload, uploading }) {
    const ref = useRef()
    return (
        <div className="col-span-2 flex items-center gap-4">
            <div className="relative">
                {photoUrl ? (
                    <img src={photoUrl} alt="Customer" className="w-16 h-16 rounded-full object-cover border-2 border-blue-500/50" />
                ) : (
                    <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center border-2 border-dashed border-white/20">
                        <Camera size={20} className="text-slate-400" />
                    </div>
                )}
                {uploading && (
                    <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                )}
            </div>
            <div>
                <button
                    type="button"
                    onClick={() => ref.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-300 px-3 py-2 rounded-xl text-xs transition"
                >
                    <Camera size={13} />
                    {photoUrl ? 'Change Photo' : 'Add Photo'}
                </button>
                {photoUrl && (
                    <button
                        type="button"
                        onClick={() => onUpload(null)}
                        className="text-xs text-red-400 hover:text-red-300 mt-1 block"
                    >
                        Remove
                    </button>
                )}
                <input
                    ref={ref}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                        const file = e.target.files[0]
                        if (file) await onUpload(file)
                        e.target.value = ''
                    }}
                />
            </div>
        </div>
    )
}

function CustomerModal({ customer, onClose, onSave, isSaving }) {
    const isEdit = !!customer?.id
    const [form, setForm] = useState({
        customer_name: customer?.customer_name || '',
        phone_number: customer?.phone_number || '',
        address: customer?.address || '',
        customer_type: customer?.customer_type || 'OTHERS',
        email: customer?.email || '',
        city: customer?.city || '',
        gstin: customer?.gstin || '',
        photo_url: customer?.photo_url || '',
        // Standard fields
        invoice_number: customer?.invoice_number || '',
        date_of_purchase: customer?.date_of_purchase
            ? (customer.date_of_purchase.includes('/') ? toIso(customer.date_of_purchase) : customer.date_of_purchase)
            : new Date().toISOString().split('T')[0],
        // BSNL fields
        telephone_number: customer?.telephone_number || '',
        bsnl_plan: customer?.bsnl_plan || '',
        connection_date: customer?.connection_date
            ? (customer.connection_date.includes('/') ? toIso(customer.connection_date) : customer.connection_date)
            : new Date().toISOString().split('T')[0],
    })
    const [uploading, setUploading] = useState(false)
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    const handlePhotoUpload = async (file) => {
        if (!file) { set('photo_url', ''); return }
        setUploading(true)
        try {
            const url = await uploadToStorage('customer-documents', 'customers', file)
            set('photo_url', url)
        } catch (e) {
            toast.error('Photo upload failed: ' + e.message)
        } finally {
            setUploading(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!form.customer_name.trim()) { toast.error('Customer name is required'); return }
        if (!form.phone_number.trim()) { toast.error('Phone number is required'); return }

        const payload = {
            customer_name: form.customer_name,
            phone_number: form.phone_number,
            address: form.address,
            customer_type: form.customer_type,
            email: form.email,
            city: form.city,
            gstin: form.gstin,
            photo_url: form.photo_url,
        }

        if (isBsnl(form.customer_type)) {
            payload.telephone_number = form.telephone_number
            payload.bsnl_plan = form.bsnl_plan
            payload.connection_date = toDdmmyyyy(form.connection_date)
        } else {
            payload.invoice_number = form.invoice_number
            payload.date_of_purchase = toDdmmyyyy(form.date_of_purchase)
        }

        onSave(payload)
    }

    const bsnlMode = isBsnl(form.customer_type)

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 rounded-2xl border border-white/8 w-full max-w-lg max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-slate-900 z-10">
                    <h2 className="font-semibold text-white">{isEdit ? 'Edit Customer' : 'New Customer'}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition"><X size={18} /></button>
                </div>
                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        {/* Photo upload */}
                        <PhotoUploadButton
                            photoUrl={form.photo_url}
                            onUpload={handlePhotoUpload}
                            uploading={uploading}
                        />

                        {/* Customer Name */}
                        <div className="col-span-2">
                            <label className="text-xs text-slate-400 block mb-1">Customer Name *</label>
                            <input
                                value={form.customer_name}
                                onChange={e => set('customer_name', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                placeholder="Full name"
                            />
                        </div>

                        {/* Phone */}
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Phone *</label>
                            <input
                                value={form.phone_number}
                                onChange={e => set('phone_number', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                placeholder="10-digit number"
                            />
                        </div>

                        {/* Customer Type */}
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Type *</label>
                            <select
                                value={form.customer_type}
                                onChange={e => set('customer_type', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                {CUSTOMER_TYPES.map(t => (
                                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                                ))}
                            </select>
                        </div>

                        {/* BSNL-specific fields */}
                        {bsnlMode ? (
                            <>
                                <div>
                                    <label className="text-xs text-slate-400 block mb-1">Telephone Number</label>
                                    <input
                                        value={form.telephone_number}
                                        onChange={e => set('telephone_number', e.target.value)}
                                        className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                        placeholder="Landline number"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400 block mb-1">BSNL Plan</label>
                                    <input
                                        value={form.bsnl_plan}
                                        onChange={e => set('bsnl_plan', e.target.value)}
                                        className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                        placeholder="Plan name"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400 block mb-1">Connection Date</label>
                                    <input
                                        type="date"
                                        value={form.connection_date}
                                        onChange={e => set('connection_date', e.target.value)}
                                        className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <label className="text-xs text-slate-400 block mb-1">Invoice Number</label>
                                    <input
                                        value={form.invoice_number}
                                        onChange={e => set('invoice_number', e.target.value)}
                                        className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                        placeholder="INV-0001"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400 block mb-1">Date of Purchase</label>
                                    <input
                                        type="date"
                                        value={form.date_of_purchase}
                                        onChange={e => set('date_of_purchase', e.target.value)}
                                        className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                                    />
                                </div>
                            </>
                        )}

                        {/* Address */}
                        <div className="col-span-2">
                            <label className="text-xs text-slate-400 block mb-1">Address</label>
                            <textarea
                                value={form.address}
                                onChange={e => set('address', e.target.value)}
                                rows={2}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 resize-none"
                                placeholder="Full address"
                            />
                        </div>

                        {/* City + Email */}
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">City</label>
                            <input
                                value={form.city}
                                onChange={e => set('city', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Email</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={e => set('email', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            />
                        </div>

                        {/* GSTIN */}
                        <div className="col-span-2">
                            <label className="text-xs text-slate-400 block mb-1">GSTIN</label>
                            <input
                                value={form.gstin}
                                onChange={e => set('gstin', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            />
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 rounded-xl text-sm transition">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving || uploading}
                            className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-medium transition"
                        >
                            <ButtonLoader loading={isSaving}>{isEdit ? 'Update' : 'Create'}</ButtonLoader>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

function CustomerDetailModal({ customer, onClose, onEdit }) {
    const bsnlMode = isBsnl(customer.customer_type)
    const color = TYPE_COLORS[customer.customer_type] || 'bg-slate-600'

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 rounded-2xl border border-white/8 w-full max-w-sm">
                <div className="flex items-center justify-between p-4 border-b border-white/5">
                    <h2 className="font-semibold text-white text-sm">Customer Details</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition"><X size={18} /></button>
                </div>
                <div className="p-5">
                    {/* Profile row */}
                    <div className="flex items-center gap-4 mb-5">
                        <CustomerAvatar customer={customer} size={16} textSize="text-2xl" className="ring-2 ring-white/10" />
                        <div>
                            <p className="font-bold text-white text-lg">{customer.customer_name}</p>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full text-white font-medium ${color}`}>
                                {customer.customer_type?.replace(/_/g, ' ')}
                            </span>
                        </div>
                    </div>

                    {/* Info */}
                    <div className="space-y-2 text-sm">
                        {customer.phone_number && (
                            <div className="flex justify-between">
                                <span className="text-slate-400">Phone</span>
                                <span className="text-white">{customer.phone_number}</span>
                            </div>
                        )}
                        {customer.city && (
                            <div className="flex justify-between">
                                <span className="text-slate-400">City</span>
                                <span className="text-white">{customer.city}</span>
                            </div>
                        )}
                        {customer.address && (
                            <div className="flex justify-between gap-4">
                                <span className="text-slate-400 shrink-0">Address</span>
                                <span className="text-white text-right">{customer.address}</span>
                            </div>
                        )}
                        {bsnlMode ? (
                            <>
                                {customer.telephone_number && (
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Tel. Number</span>
                                        <span className="text-white">{customer.telephone_number}</span>
                                    </div>
                                )}
                                {customer.bsnl_plan && (
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">BSNL Plan</span>
                                        <span className="text-white">{customer.bsnl_plan}</span>
                                    </div>
                                )}
                                {customer.connection_date && (
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Connection Date</span>
                                        <span className="text-white">{formatDateIn(customer.connection_date)}</span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {customer.invoice_number && (
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Invoice</span>
                                        <span className="text-white">{customer.invoice_number}</span>
                                    </div>
                                )}
                                {customer.date_of_purchase && (
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Purchase Date</span>
                                        <span className="text-white">{formatDateIn(customer.date_of_purchase)}</span>
                                    </div>
                                )}
                            </>
                        )}
                        {customer.email && (
                            <div className="flex justify-between">
                                <span className="text-slate-400">Email</span>
                                <span className="text-white truncate ml-4">{customer.email}</span>
                            </div>
                        )}
                        {customer.gstin && (
                            <div className="flex justify-between">
                                <span className="text-slate-400">GSTIN</span>
                                <span className="text-white">{customer.gstin}</span>
                            </div>
                        )}
                        {(customer.outstanding_balance > 0) && (
                            <div className="flex justify-between">
                                <span className="text-slate-400">Outstanding</span>
                                <span className="text-red-400 font-medium">₹{parseFloat(customer.outstanding_balance).toLocaleString('en-IN')}</span>
                            </div>
                        )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-3 mt-5">
                        {customer.phone_number && (
                            <>
                                <a
                                    href={`tel:${customer.phone_number}`}
                                    className="flex-1 flex items-center justify-center gap-2 bg-green-600/20 hover:bg-green-600/30 text-green-400 py-2.5 rounded-xl text-sm font-medium transition"
                                >
                                    <Phone size={15} />
                                    Call
                                </a>
                                <a
                                    href={`https://wa.me/91${customer.phone_number.replace(/\D/g, '').slice(-10)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 flex items-center justify-center gap-2 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 py-2.5 rounded-xl text-sm font-medium transition"
                                >
                                    <MessageCircle size={15} />
                                    WhatsApp
                                </a>
                            </>
                        )}
                        <button
                            onClick={onEdit}
                            className="flex-1 flex items-center justify-center gap-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 py-2.5 rounded-xl text-sm font-medium transition"
                        >
                            <Edit2 size={15} />
                            Edit
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default function Customers() {
    const qc = useQueryClient()
    const [search, setSearch] = useState('')
    const [typeFilter, setTypeFilter] = useState('ALL')
    const [sortAsc, setSortAsc] = useState(false)
    const [modal, setModal] = useState(null)    // null | 'new' | customer (for edit)
    const [detail, setDetail] = useState(null)  // customer detail view

    const { data: customers = [], isLoading } = useQuery({
        queryKey: ['customers', search, typeFilter],
        queryFn: () => getCustomers(search, typeFilter === 'ALL' ? '' : typeFilter),
    })

    const createMut = useMutation({
        mutationFn: createCustomer,
        onSuccess: () => { qc.invalidateQueries(['customers']); setModal(null); toast.success('Customer added') },
        onError: e => toast.error(e.message),
    })
    const updateMut = useMutation({
        mutationFn: ({ id, ...r }) => updateCustomer(id, r),
        onSuccess: () => { qc.invalidateQueries(['customers']); setModal(null); setDetail(null); toast.success('Customer updated') },
        onError: e => toast.error(e.message),
    })
    const deleteMut = useMutation({
        mutationFn: deleteCustomer,
        onSuccess: () => { qc.invalidateQueries(['customers']); toast.success('Deleted') },
        onError: e => toast.error(e.message),
    })

    const sorted = [...customers].sort((a, b) => {
        const da = new Date(a.created_at), db = new Date(b.created_at)
        return sortAsc ? da - db : db - da
    })

    const filterChips = ['ALL', ...CUSTOMER_TYPES]

    return (
        <StaffLayout>
            <div className="space-y-5">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-white">
                        Customers
                        <span className="text-slate-500 font-normal text-base ml-2">({customers.length})</span>
                    </h1>
                    <button
                        onClick={() => setModal('new')}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-medium transition"
                    >
                        <Plus size={16} /> Add Customer
                    </button>
                </div>

                {/* Search + sort */}
                <div className="flex gap-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search by name..."
                            className="w-full bg-slate-900 border border-white/5 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <button
                        onClick={() => setSortAsc(!sortAsc)}
                        className="flex items-center gap-2 bg-slate-900 border border-white/5 rounded-xl px-3 py-2.5 text-sm text-slate-400 hover:text-white transition"
                    >
                        <ArrowUpDown size={14} />
                        {sortAsc ? 'Oldest' : 'Newest'}
                    </button>
                </div>

                {/* Type filter chips */}
                <div className="flex flex-wrap gap-2">
                    {filterChips.map(type => (
                        <button
                            key={type}
                            onClick={() => setTypeFilter(type)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-medium transition ${typeFilter === type
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-900 border border-white/5 text-slate-400 hover:text-white'
                                }`}
                        >
                            {type === 'ALL' ? 'All' : type.replace(/_/g, ' ')}
                        </button>
                    ))}
                </div>

                {/* Customer cards grid */}
                {isLoading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Array(6).fill(0).map((_, i) => (
                            <div key={i} className="bg-slate-900 rounded-xl border border-white/5 p-4 animate-pulse h-28" />
                        ))}
                    </div>
                ) : sorted.length === 0 ? (
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-12 text-center text-slate-500">
                        No customers found
                    </div>
                ) : (
                    <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {sorted.map(c => {
                            const color = TYPE_COLORS[c.customer_type] || 'bg-slate-600'
                            return (
                                <StaggerItem key={c.id}>
                                <div
                                    onClick={() => setDetail(c)}
                                    className="bg-slate-900 rounded-xl border border-white/5 hover:border-white/20 hover:shadow-lg hover:shadow-black/10 p-4 cursor-pointer transition-all duration-200 group"
                                >
                                    <div className="flex items-start gap-3">
                                        {/* Avatar */}
                                        <CustomerAvatar customer={c} size={11} textSize="text-lg" />

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className="font-semibold text-white truncate">{c.customer_name}</p>
                                                <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition">
                                                    <button
                                                        onClick={e => { e.stopPropagation(); setModal(c) }}
                                                        className="text-slate-400 hover:text-blue-400 p-1"
                                                    >
                                                        <Edit2 size={12} />
                                                    </button>
                                                    <button
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            if (window.confirm('Delete customer?')) deleteMut.mutate(c.id)
                                                        }}
                                                        className="text-slate-400 hover:text-red-400 p-1"
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                </div>
                                            </div>
                                            <p className="text-xs text-slate-400 mt-0.5">{c.phone_number}</p>
                                            {c.city && <p className="text-xs text-slate-500 mt-0.5">{c.city}</p>}
                                            <div className="flex items-center gap-2 mt-2">
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full text-white font-medium ${color}`}>
                                                    {c.customer_type?.replace(/_/g, ' ')}
                                                </span>
                                                {c.outstanding_balance > 0 && (
                                                    <span className="text-[10px] text-red-400">
                                                        ₹{parseFloat(c.outstanding_balance).toLocaleString('en-IN')} due
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Quick actions on hover */}
                                    {c.phone_number && (
                                        <div className="flex gap-2 mt-3 pt-3 border-t border-white/5 opacity-0 group-hover:opacity-100 transition">
                                            <a
                                                href={`tel:${c.phone_number}`}
                                                onClick={e => e.stopPropagation()}
                                                className="flex-1 flex items-center justify-center gap-1 bg-green-600/15 text-green-400 py-1.5 rounded-lg text-xs font-medium hover:bg-green-600/25 transition"
                                            >
                                                <Phone size={11} /> Call
                                            </a>
                                            <a
                                                href={`https://wa.me/91${c.phone_number.replace(/\D/g, '').slice(-10)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={e => e.stopPropagation()}
                                                className="flex-1 flex items-center justify-center gap-1 bg-emerald-600/15 text-emerald-400 py-1.5 rounded-lg text-xs font-medium hover:bg-emerald-600/25 transition"
                                            >
                                                <MessageCircle size={11} /> WhatsApp
                                            </a>
                                        </div>
                                    )}
                                </div>
                                </StaggerItem>
                            )
                        })}
                    </StaggerContainer>
                )}
            </div>

            {/* Add / Edit modal */}
            <ModalOverlay show={!!modal}>
                {modal && (
                    <CustomerModal
                        customer={modal === 'new' ? null : modal}
                        onClose={() => setModal(null)}
                        onSave={data => modal === 'new' ? createMut.mutate(data) : updateMut.mutate({ id: modal.id, ...data })}
                        isSaving={createMut.isLoading || updateMut.isLoading}
                    />
                )}
            </ModalOverlay>

            {/* Detail modal */}
            <ModalOverlay show={!!(detail && !modal)}>
                {detail && !modal && (
                    <CustomerDetailModal
                        customer={detail}
                        onClose={() => setDetail(null)}
                        onEdit={() => { setModal(detail); setDetail(null) }}
                    />
                )}
            </ModalOverlay>
        </StaffLayout>
    )
}
